import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, Store, ImagePlus } from "lucide-react";

export default function AdminSucursales() {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", image_path: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sucursales");
      setSucursales(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", image_path: null });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, image_path: s.image_path || null });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/sucursales/${editing.id}`, form);
        setSucursales((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Sucursal actualizada");
      } else {
        const { data } = await api.post("/sucursales", form);
        setSucursales((p) => [...p, data]);
        toast.success("Sucursal creada");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar la sucursal "${s.name}"?`)) return;
    try {
      await api.delete(`/sucursales/${s.id}`);
      setSucursales((p) => p.filter((x) => x.id !== s.id));
      toast.success("Sucursal eliminada");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#4A3B32]" data-testid="sucursales-title">
            Sucursales
          </h1>
          <p className="text-[#8A796F]">Crea, edita y sube fotos de cada local.</p>
        </div>
        <Button onClick={openNew} data-testid="sucursal-create-button" className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold px-6">
          <Plus className="w-4 h-4 mr-2" /> Nueva sucursal
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sucursales.map((s) => (
          <div key={s.id} className="bg-white border border-[#EADBCE] rounded-2xl overflow-hidden" data-testid={`sucursal-row-${s.id}`}>
            <div className="aspect-video border-b border-[#EADBCE] flex items-center justify-center overflow-hidden">
              {s.image_path ? (
                <img src={fileUrl(s.image_path)} alt={s.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#F7C8E0] via-[#FFE7DA] to-[#A7E4D3] flex items-center justify-center">
                  <Store className="w-10 h-10 text-white/80 drop-shadow" />
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-display font-bold text-lg text-[#4A3B32]">{s.name}</h3>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`sucursal-edit-${s.id}`} className="rounded-full">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`sucursal-delete-${s.id}`} className="rounded-full text-[#FF5C5C] hover:text-[#FF5C5C] hover:bg-[#FFF0F0]">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-[#EADBCE] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Editar sucursal" : "Nueva sucursal"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Foto de la sucursal</Label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-24 rounded-xl bg-[#FCF9F2] border border-[#EADBCE] overflow-hidden flex items-center justify-center">
                  {form.image_path ? (
                    <img src={fileUrl(form.image_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Store className="w-6 h-6 text-[#8A796F]" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileRef}
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="sucursal-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-full border-[#EADBCE]"
                    data-testid="sucursal-image-upload-button"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                    {form.image_path ? "Cambiar foto" : "Subir foto"}
                  </Button>
                  <p className="text-xs text-[#8A796F] mt-1">JPG, PNG, WebP. Max 5MB.</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Sucursal Centro"
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="sucursal-name-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold"
              data-testid="sucursal-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
